// @flow
import * as React from 'react'
import is from 'mz-utils/is'
import VirtualList from 'react-tiny-virtual-list'
import AutoSizer from './AutoSizer'
import OptionItem from '../OptionItem'
import { Container } from './elements'

// Types
import type { Props as NaiveListRendererProps } from '../NaiveListRenderer'

export type Props = NaiveListRendererProps

/**
 * Returns actual option height based on given option size.
 * Required because optionSize could be number, array or function.
 * @param  {number} index
 * @param  {any} optionSize
 * @return {number}
 */
const getOptionSize = (index, optionSize) => {
  if (is.number(optionSize)) {
    return optionSize
  }
  if (optionSize && is.func(optionSize)) {
    return optionSize(index)
  }
  if (optionSize && is.array(optionSize)) {
    return optionSize[index] || 20
  }
  return 20
}

/**
 * Calculate default height of the container based on given
 * max container height. Required to set correct container
 * height when total options height is less than maxHeight
 * (to avoid empty space after last option in the short list)
 * @param  {string} maxHeight
 * @param  {Array<any>} options
 * @param  {any} optionSize
 * @return {string}
 */
const getContainerHeight = (maxHeight, options, optionSize) => {
  const intMaxHeight = parseInt(maxHeight, 10)
  if (isNaN(intMaxHeight)) return 'auto' // eslint-disable-line no-restricted-globals

  let finalHeight = 0
  for (let i = 0; i < options.length; i++) {
    finalHeight += getOptionSize(i, optionSize)
    if (finalHeight >= intMaxHeight) {
      return `${intMaxHeight}px`
    }
  }

  return `${finalHeight}px`
}

/**
 * Naive options list renderer that just renders a whole list
 * of options. Could be slow for large lists.
 * @return {React.Element}
 */
const LazyListRenderer = ({
  maxHeight = '300px',
  focusedIndex,
  onOptionClick,
  renderOption,
  OptionWrapper,
  options,
  optionSize,
}: Props) => (
  <Container
    style={{ height: getContainerHeight(maxHeight, options, optionSize) }}
  >
    <AutoSizer disableWidth>
      {({ height }) => (
        <VirtualList
          width={'100%'}
          height={height}
          itemCount={options.length}
          itemSize={optionSize}
          renderItem={({ index, style }) => (
            <OptionItem
              key={index}
              index={index}
              style={style}
              value={options[index]}
              focused={focusedIndex === index}
              onClick={onOptionClick}
              renderOption={renderOption}
              OptionWrapper={OptionWrapper}
            />
          )}
        />
      )}
    </AutoSizer>
  </Container>
)

export default LazyListRenderer
